import { Routes, Route, useLocation } from 'react-router-dom';
import { useAuth } from './context/Auth';

import { MainContainer } from './components/Nav/MainContainer';
import { PrivateRoute } from './components/PrivateRoute';
import ErrorPage from './components/ErrorPage/ErrorPage';
import Logout from './components/Logout/Logout';

import { MarketDashboard, MarketDetail } from './components/Market';
import { StrategiesPage, StrategyDetail } from './components/Strategies';

import { SignUpPage } from './pages/SignUp/SignUpContext';
import ChooseUsername from './pages/ChooseUsername/ChooseUsername';
import UploadImage from './pages/UploadImage/UploadImage';
import ChoosePlan from './pages/ChoosePlan/ChoosePlan';
import OnBoarding from './pages/Onboarding/Onboarding';
import ChooseTheme from './pages/ChooseTheme/ChooseTheme';

import { SignInPage } from './pages/SignIn/SignInContext';
import ChooseAccount from './pages/ChooseAccount/ChooseAccount';
import { Account } from './pages/Account';
import Referral from './pages/Referral/Referral';
import Support from './pages/Support/Support';
import CheckoutError from './pages/Subscriptions/CheckoutError';
import Feedback from './pages/Feedback/Feedback';
import Payments from './pages/Subscriptions/Payments';
import Contacts from './pages/Contacts/Contacts';
import StaticPage from './pages/StaticPage/StaticPage';
import PrivacyAndTerms from './pages/PrivacyAndTerms/PrivacyAndTerms';
import Subscriptions from './pages/Subscriptions/Subscriptions';
import CheckoutSuccess from './pages/Subscriptions/CheckoutSuccess';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import Inspiration from './pages/Inspiration/Inspiration';

import { UploadImageProvider } from './context/UploadImage';
import RegistrationCompleted from './components/Registration/RegistrationCompleted';
import ChatWindowed from './components/WindowWidget/Chat/ChatWindowed';

import AccountRecoverModal from './components/AccountRecoverModal/AccountRecoverModal';
import Devices from './components/Devices/Devices';
import WalletConnectReturn from './components/Asset/WalletAsset/WalletConnectReturn';
import ExpiredModal from './components/ExpirationWarning/ExpiredModal';
import { trackPageView } from './helpers/analytics';
import { MobileBrowserAlert } from './components/MobileBrowserAlert';

type TRoute = {
  path: string;
  key: string;
  component: JSX.Element;
  private: boolean;
  navbar: 'limited' | 'default' | 'hidden';
  variant: 'white' | 'black';
};

const routes: TRoute[] = [
  //////////// PUBLIC ROUTES
  {
    path: '/',
    key: 'home',
    component: <SignUpPage />,
    private: false,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/signin',
    key: 'signin',
    component: <SignInPage />,
    private: false,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/signup',
    key: 'signup',
    component: <SignUpPage />,
    private: false,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/signup/referralCode/:referralCode',
    key: 'signup',
    component: <SignUpPage />,
    private: false,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/forgot-password',
    key: 'forgotPassword',
    component: <ForgotPassword />,
    private: false,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/terms-and-conditions',
    key: 'termsAndConditions',
    component: <StaticPage slug={'terms'} />,
    private: false,
    navbar: 'limited',
    variant: 'white',
  },
  {
    path: '/privacy-policy',
    key: 'privacyPolicy',
    component: <StaticPage slug={'privacy-policy'} />,
    private: false,
    navbar: 'limited',
    variant: 'white',
  },

  //////////// PRIVATE ROUTES
  {
    path: '/logout',
    key: 'logout',
    component: <Logout />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/choose-username',
    key: 'choose-username',
    component: <ChooseUsername />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/choose-account',
    key: 'choose-account',
    component: <ChooseAccount />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/choose-theme',
    key: 'choose-theme',
    component: <ChooseTheme />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/choose-plan',
    key: 'choose-plan',
    component: <ChoosePlan />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/upload-image',
    key: 'upload-image',
    component: (
      <UploadImageProvider>
        <UploadImage />
      </UploadImageProvider>
    ),
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/welcome',
    key: 'welcome',
    component: <OnBoarding />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/registration-completed',
    key: 'registrationCompleted',
    component: <RegistrationCompleted />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/account',
    key: 'account',
    component: <Account />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/referral',
    key: 'referral',
    component: <Referral />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/subscriptions',
    key: 'subscriptions',
    component: <Subscriptions />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/checkout-success',
    key: 'checkoutSuccess',
    component: <CheckoutSuccess />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/checkout-error',
    key: 'checkoutError',
    component: <CheckoutError />,
    private: true,
    navbar: 'limited',
    variant: 'black',
  },
  {
    path: '/support',
    key: 'support',
    component: <Support />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/devices',
    key: 'devices',
    component: <Devices />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/contacts',
    key: 'contacts',
    component: <Contacts />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/privacy-and-terms',
    key: 'privacy-and-terms',
    component: <PrivacyAndTerms />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/strategies',
    key: 'strategies',
    component: <StrategiesPage />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/strategies/:id',
    key: 'strategyDetail',
    component: <StrategyDetail />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/wallet-connect',
    key: 'walletConnect',
    component: <WalletConnectReturn />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/markets',
    key: 'marketList',
    component: <MarketDashboard />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/market/:id/:type/:filter/:page',
    key: 'marketCryptoDetail',
    component: <MarketDetail />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },

  {
    path: '/inspiration',
    key: 'widgetsGrid',
    component: <Inspiration />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/chat',
    key: 'chat',
    component: <ChatWindowed />,
    private: true,
    navbar: 'hidden',
    variant: 'white',
  },
  {
    path: '404',
    key: 'error',
    component: <ErrorPage code={404} />,
    private: false,
    navbar: 'hidden',
    variant: 'black',
  },
  {
    path: '*',
    key: 'error',
    component: <ErrorPage code={404} />,
    private: false,
    navbar: 'hidden',
    variant: 'black',
  },
  {
    path: '/feedback',
    key: 'feedback',
    component: <Feedback />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/payments',
    key: 'payments',
    component: <Payments />,
    private: true,
    navbar: 'default',
    variant: 'white',
  },
  {
    path: '/mobile-alert',
    key: 'mobile-alert',
    component: <MobileBrowserAlert />,
    private: true,
    navbar: 'hidden',
    variant: 'black',
  },
];

export const AppRoutes = () => {
  const { user, refetch } = useAuth();
  const location = useLocation();

  trackPageView(location.pathname);

  if (user && user.removalStatus) {
    return <AccountRecoverModal refetchUser={refetch} />;
  }

  if (false) return <ExpiredModal />; // TODO:vaniko

  return (
    <Routes>
      {routes.map((r) => {
        if (r.private) {
          return (
            <Route
              path={r.path}
              key={r.key}
              element={
                <PrivateRoute>
                  <MainContainer view={r.navbar} variant={r.variant}>
                    {r.component}
                  </MainContainer>
                </PrivateRoute>
              }
            />
          );
        } else {
          return (
            <Route
              path={r.path}
              key={r.key}
              element={
                <MainContainer view={r.navbar} variant={r.variant}>
                  {r.component}
                </MainContainer>
              }
            />
          );
        }
      })}
    </Routes>
  );
};
